import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import type { TranscriptionChannelManager } from "@/store/audio-capture/transcription/interfaces";

export class TranscriptionCoordinator {
  constructor(private readonly getManagers: () => TranscriptionChannelManager[]) {}

  private get managers(): TranscriptionChannelManager[] {
    return (this.getManagers() ?? []).filter(Boolean);
  }

  initializeTranscriptions({ clearTranscript }: { clearTranscript?: boolean } = {}): void {
    this.managers.forEach(manager => manager.initializeTranscription?.({ clearTranscript }));
  }

  startTranscriptions(): void {
    this.managers.forEach(manager => manager.startTranscription?.());
  }

  stopTranscriptions(): void {
    this.managers.forEach(manager => manager.stopTranscription?.());
  }

  pauseTranscriptions(): void {
    this.managers.forEach(manager => manager.pauseTranscription?.());
  }

  resumeTranscriptions(): void {
    this.managers.forEach(manager => manager.resumeTranscription?.());
  }

  resetTranscriptions({ clearTranscript }: { clearTranscript?: boolean } = {}): void {
    this.managers.forEach(manager => manager.reset?.({ clearTranscript }));
  }

  flushAudioBuffers(): void {
    this.managers.forEach(manager => manager.flushAudioBuffer?.());
  }

  initializeWebSockets(): void {
    this.managers.forEach(manager => {
      if (typeof manager.initializeWebSocket !== "function") return;
      void manager.initializeWebSocket().catch(error =>
        logger.error({
          message: "[TranscriptionCoordinator] initializeWebSocket failed",
          info: { error: objectModule.safeErrorAsJson(error as Error), channel: manager.channel },
        })
      );
    });
  }

  async cleanupWebSocketAndAudio(): Promise<void> {
    await Promise.all(
      this.managers.map(async manager => {
        if (typeof manager.cleanupWebSocketAndAudio !== "function") return;
        try {
          await manager.cleanupWebSocketAndAudio();
        } catch (error) {
          logger.error({
            message: "[TranscriptionCoordinator] cleanupWebSocketAndAudio failed",
            info: { error: objectModule.safeErrorAsJson(error as Error), channel: manager.channel },
          });
        }
      })
    );
  }
}
